import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { Button, Input, Select, Badge } from '../../components/ui';
import { Modal } from '../../components/ui/Modal';
import { Plus, Edit2, Trash2, Printer, Search, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function WarningsTab() {
  const { selectedYear } = useAuth();
  const navigate = useNavigate();
  const [warnings, setWarnings] = useState([]);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    student_id: '',
    reason: '',
    punishment_start_date: '',
    punishment_days: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPunishment, setIsPunishment] = useState(false); // To show extra fields

  useEffect(() => {
    if (selectedYear) {
      fetchWarnings();
      fetchStudents();
    }
  }, [selectedYear]);

  const fetchWarnings = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/warnings', {
        params: { academic_year_id: selectedYear?.id }
      });
      setWarnings(response.data);
    } catch (error) {
      console.error('Error fetching warnings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await api.get('/students', { params: { all: true } });
      setStudents(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const checkIsPunishment = (studentId) => {
    if (!studentId) return false;
    const count = warnings.filter(w => w.student_id == studentId).length;
    return count >= 2;
  };

  const handleStudentChange = (e) => {
    const studentId = e.target.value;
    setFormData(prev => ({ ...prev, student_id: studentId }));
    if (!formData.id) {
      setIsPunishment(checkIsPunishment(studentId));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (formData.id) {
        await api.put(`/warnings/${formData.id}`, formData);
      } else {
        await api.post('/warnings', {
          ...formData,
          academic_year_id: selectedYear?.id
        });
      }
      setIsModalOpen(false);
      fetchWarnings();
      setFormData({ id: null, student_id: '', reason: '', punishment_start_date: '', punishment_days: '' });
      setIsPunishment(false);
    } catch (error) {
      console.error('Error saving warning:', error);
      alert('حدث خطأ أثناء الحفظ');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (warning) => {
    setFormData({
      id: warning.id,
      student_id: warning.student_id,
      reason: warning.reason,
      punishment_start_date: warning.punishment_start_date ? warning.punishment_start_date.substring(0,10) : '',
      punishment_days: warning.punishment_days || ''
    });
    setIsPunishment(warning.is_punishment);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا السجل؟')) {
      try {
        await api.delete(`/warnings/${id}`);
        fetchWarnings();
      } catch (error) {
        console.error('Error deleting warning:', error);
      }
    }
  };

  const handlePrint = (warning) => {
    if (warning.is_punishment) {
      navigate(`/print/punishment/${warning.id}`);
    } else {
      navigate(`/print/warning/${warning.id}`);
    }
  };

  const filteredWarnings = warnings.filter(w => 
    w.student?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.reason?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const studentOptions = students.map(s => ({ value: s.id, label: s.qr_code ? `${s.full_name} - ${s.qr_code}` : s.full_name }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="بحث باسم التلميذ أو السبب..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-4 pr-10 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
          />
        </div>
        <Button onClick={() => {
          setFormData({ id: null, student_id: '', reason: '', punishment_start_date: '', punishment_days: '' });
          setIsPunishment(false);
          setIsModalOpen(true);
        }}>
          <Plus className="w-4 h-4 ml-2" />
          إضافة إنذار / عقوبة
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-right text-sm">
          <thead className="bg-slate-50 text-slate-600 font-medium border-b border-slate-200">
            <tr>
              <th className="px-6 py-4">التلميذ</th>
              <th className="px-6 py-4">القسم</th>
              <th className="px-6 py-4">السبب</th>
              <th className="px-6 py-4">النوع</th>
              <th className="px-6 py-4">التفاصيل</th>
              <th className="px-6 py-4">التاريخ</th>
              <th className="px-6 py-4 text-center">الإجراءات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-slate-500">جاري التحميل...</td>
              </tr>
            ) : filteredWarnings.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-slate-500">لا توجد بيانات</td>
              </tr>
            ) : (
              filteredWarnings.map((warning) => (
                <tr key={warning.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 font-bold text-slate-800">{warning.student?.full_name}</td>
                  <td className="px-6 py-4 text-slate-600">{warning.student?.school_class?.name || '-'}</td>
                  <td className="px-6 py-4 text-slate-600 max-w-xs truncate" title={warning.reason}>{warning.reason}</td>
                  <td className="px-6 py-4">
                    {warning.is_punishment ? (
                      <Badge variant="danger">عقوبة (الثالث)</Badge>
                    ) : (
                      <Badge variant="warning">إنذار {warning.warning_number}</Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-slate-500 text-xs">
                    {warning.is_punishment && warning.punishment_start_date ? (
                      <div>
                        <div>من: {warning.punishment_start_date.substring(0,10)}</div>
                        <div className="font-bold text-red-500">المدة: {warning.punishment_days} أيام</div>
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 text-slate-500">{new Date(warning.created_at).toLocaleDateString('ar-MA')}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => handlePrint(warning)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors" title="طباعة">
                        <Printer className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleEdit(warning)} className="p-1.5 text-amber-500 hover:bg-amber-50 rounded-lg transition-colors" title="تعديل">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(warning.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="حذف">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={formData.id ? 'تعديل السجل' : 'إضافة إنذار جديد'}>
        <form onSubmit={handleSubmit} className="space-y-4 p-1">
          <Select
            label="التلميذ"
            options={studentOptions}
            value={formData.student_id}
            onChange={handleStudentChange}
            disabled={!!formData.id} // لا يمكن تغيير التلميذ في حالة التعديل
            required
          />
          
          <div className="space-y-1.5">
            <label className="text-xs text-slate-800 ml-1">سبب المخالفة</label>
            <textarea
              required
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className="w-full bg-slate-50/50 border border-slate-200 rounded-lg p-3 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary h-24 resize-none"
              placeholder="اكتب تفاصيل المخالفة هنا..."
            />
          </div>

          {isPunishment && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 space-y-4 mt-2">
              <div className="flex items-center gap-2 text-red-600 mb-2">
                <ShieldAlert className="w-5 h-5" />
                <span className="font-bold text-sm">هذا هو الإنذار الثالث! سيتم تحويله إلى عقوبة تأديبية.</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="تاريخ بداية العقوبة"
                  type="date"
                  required
                  value={formData.punishment_start_date}
                  onChange={(e) => setFormData({ ...formData, punishment_start_date: e.target.value })}
                />
                <Input
                  label="عدد أيام التوقيف"
                  type="number"
                  min="1"
                  required
                  value={formData.punishment_days}
                  onChange={(e) => setFormData({ ...formData, punishment_days: e.target.value })}
                />
              </div>
            </div>
          )}

          <div className="pt-4 flex gap-3">
            <Button type="submit" isLoading={isSubmitting} className="flex-1">
              حفظ
            </Button>
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)} className="flex-1">
              إلغاء
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
